import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { ButtonMedium } from '@components/core-ui/form-elements/buttons/Button/variations/PrimaryButtonSizes';
import { PhoneInput } from '@components/core-ui/form-elements/PhoneInput';
import PhoneIcon from '@components/icons/Phone.svg';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useMutation } from '@tanstack/react-query';
import React, { useRef, useState } from 'react';
import { checkPhoneNumber } from '../../../../api/checkPhoneNumber';
import { sendOtpCode } from '../../../../api/sendOtpCode';
import { getRequestErrorMessage } from '../../../../helpers/getRequestErrorMessage';
import { isLocalTestHost } from '../../../../helpers/isLocalTestHost';
import { usePageContext } from '../../../../providers/PageProvider';

export const RegistrationPagePhoneStage = () => {
  const { call } = useAPI();
  const {
    setStage,
    setCodeSentDate,
    phone,
    setPhone,
    goToLogin,
    register,
    isLoading: isRegistering,
  } = usePageContext();
  const { isPending: isCheckingPhone, mutateAsync: checkPhone } = useMutation({
    mutationFn: (phoneNumber: string) => checkPhoneNumber({ call, requestBody: { phone: phoneNumber } }),
  });
  const { isPending: isSendingCode, mutateAsync: sendCode } = useMutation({
    mutationFn: (phoneNumber: string) => sendOtpCode({ call, requestBody: { phone: phoneNumber } }),
  });
  const isSubmitInFlight = useRef(false);
  const [error, setError] = useState<string | undefined>();
  const [isPhoneInvalid, setIsPhoneInvalid] = useState<boolean>(false);
  const isLoading = isCheckingPhone || isSendingCode || isRegistering;

  const handleFormSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    if (isSubmitInFlight.current) {
      return;
    }
    isSubmitInFlight.current = true;
    setError(undefined);

    try {
      if (isLocalTestHost(window.location.hostname)) {
        await register();
        return;
      }
      const response = await checkPhone(phone);
      if (!response.valid) {
        setIsPhoneInvalid(true);
        setError('Phone number is not valid or already in use');
        return;
      }
      await sendCode(phone);
      setCodeSentDate(Date.now());
      setStage('signupCode');
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError));
    } finally {
      isSubmitInFlight.current = false;
    }
  };

  const isButtonDisabled = !phone?.length || isPhoneInvalid || isLoading;

  return (
    <div className="root">
      <h1 className="header">Verify Your Phone Number</h1>

      <form onSubmit={handleFormSubmit}>
        {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
        <div className="margin20">
          <PhoneInput
            onChange={(value) => {
              setError(undefined);
              setIsPhoneInvalid(false);
              setPhone(value);
            }}
            defaultCountry="US"
            name="phone"
            placeholder="Phone number (e.g. +12223334455)"
            value={phone || ''}
            required={true}
            icon={PhoneIcon}
          />
        </div>

        <ButtonMedium disabled={isButtonDisabled} className="mb-[30px] mx-auto w-full" type="submit">
          Next
        </ButtonMedium>

        {error ? (
          <div className="formError" data-testid="errorMessage">
            <ErrorMessage error={error} />
          </div>
        ) : null}
      </form>

      <p className="haveAnAccount">
        Have an account?&nbsp;&nbsp;
        <span onClick={goToLogin} className="signIn">
          Sign In
        </span>
      </p>
    </div>
  );
};
